import { useEffect, useState } from "react";
import type { AgenticCartBundleSummaryResponse } from "@ishvaram/api-client";
import {
  checkCartConfirmation,
  forgetCartConfirmation,
  readCartConfirmation,
} from "./AgenticCartCheckout.confirmation";
import type { AgenticCartCallbacks, CartStatus } from "./AgenticCartCheckout.types";

export function useCartConfirmation(
  bundle: AgenticCartBundleSummaryResponse | null,
  status: CartStatus,
  { onStatus, onSuccess, onError }: Omit<AgenticCartCallbacks, "onRecovery">
) {
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (
      !bundle ||
      bundle.requires_shipping ||
      bundle.sankalp_program ||
      status !== "verification_pending"
    )
      return;
    const saved = readCartConfirmation(bundle.bundle_id);
    if (!saved) return;
    const confirmation = saved;
    let active = true;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let polls = 0;
    async function check() {
      const result = await checkCartConfirmation(confirmation);
      if (!active) return;
      if (result === "refunded") {
        forgetCartConfirmation();
        onError("This payment was refunded. You can start a new payment.");
        onStatus("ready");
      } else if (result) {
        onSuccess(result);
        onError("");
        onStatus("success");
      } else {
        onError("Payment confirmation is pending. Do not pay again. Contact support.");
        onStatus("verification_pending");
        if (++polls < 5) timer = setTimeout(() => void check(), 15000);
      }
    }
    void check();
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [bundle, status, attempt, onStatus, onSuccess, onError]);
  return () => setAttempt((value) => value + 1);
}
